import React from 'react';
import MultiSelectDropdown from './MultiSelectDropdown';

// Mirrors models.DocumentStatus. Order is the lifecycle order, which is also
// the order the dashboard tiles use.
export const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'archived', label: 'Archived' },
];

export const ALL_STATUSES = STATUS_OPTIONS.map((o) => o.value);

/**
 * The default view: everything except Archived. An archived policy is one
 * somebody deliberately took out of service, so it is noise in the day-to-day
 * list -- but it is never deleted, so it has to stay one checkbox away.
 */
export const DEFAULT_STATUSES = ALL_STATUSES.filter((v) => v !== 'archived');

export function isDefaultSelection(values) {
  return values.length === DEFAULT_STATUSES.length
    && DEFAULT_STATUSES.every((v) => values.includes(v));
}

function summarize(values) {
  if (values.length === ALL_STATUSES.length) return 'All statuses';
  if (isDefaultSelection(values)) return 'All except Archived';
  if (values.length === 1) {
    return STATUS_OPTIONS.find((o) => o.value === values[0])?.label || values[0];
  }
  return `${values.length} statuses`;
}

/**
 * Statuses filter for the Documents page (GitHub #36) -- a multi-select where
 * there used to be a single-choice <select> whose "All Statuses" included
 * archived documents.
 *
 * Built on MultiSelectDropdown rather than LookupMultiSelect: the options are a
 * fixed enum, not rows fetched from an API, so there is nothing to load and
 * nothing to filter.
 *
 * The last remaining checkbox is DISABLED rather than allowed to be unticked.
 * An empty selection would have to mean either "no documents" or "no filter",
 * and there is no way for the URL to say which -- so it is simply made
 * unreachable instead of being given a sentinel.
 */
function StatusMultiSelect({ value, onChange }) {
  const toggle = (status) => {
    if (value.includes(status)) {
      if (value.length === 1) return;
      onChange(value.filter((v) => v !== status));
    } else {
      onChange(ALL_STATUSES.filter((v) => value.includes(v) || v === status));
    }
  };

  return (
    <MultiSelectDropdown label={summarize(value)}>
      <div className="multiselect-options" role="listbox" aria-multiselectable="true">
        {STATUS_OPTIONS.map((option) => {
          const checked = value.includes(option.value);
          const isLast = checked && value.length === 1;
          return (
            <label
              key={option.value}
              className="multiselect-option"
              title={isLast ? 'At least one status must stay selected' : undefined}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={isLast}
                onChange={() => toggle(option.value)}
              />
              {option.label}
            </label>
          );
        })}
      </div>
      {!isDefaultSelection(value) && (
        <button
          type="button"
          className="multiselect-clear"
          onClick={() => onChange(DEFAULT_STATUSES)}
        >
          Reset to all except Archived
        </button>
      )}
    </MultiSelectDropdown>
  );
}

export default StatusMultiSelect;
